/**
 * ContextFormView — a page's FORM view: the fields a page needs from the
 * person, drawn as controls, each value published to ViewManager as the
 * selection of a pseudo-view (`contextViewId(view.id, field.name)`), so the
 * views the interpreter wired to it filter and refresh exactly as they do for
 * a selected grid row (FEATURE-20261014-page-dsl-reference-field-is-picker-or-filter).
 *
 * A reference field is a ReferencePicker over its target; its "row" is
 * `{ <targetKey>: key, <display>: text }`, which is what a drill-down filter
 * reads (`parentField` = the target's key) and what form lineage carries. A
 * plain field is a text / number / toggle control whose "row" is
 * `{ value }`. Every value also travels in the route's query string under the
 * field's `param`, so a drilled page survives a reload and can be shared.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import type { GridApi } from "@mui/x-data-grid";
import type { ContextFieldDef, SqlFirewallView } from "../../../model/qubes/qubes-model";
import { contextViewId } from "../../../model/qubes/qubes-model";
import type { TableModel } from "../../qubes-table/table-model";
import { ViewManager } from "../../../services/view-manager";
import { ReferencePicker } from "../input/reference-picker";
import { lookupReference } from "../input/reference-query";
import type { ReferenceValue } from "../input/reference-query";
import "../renderer/form-renderer.scss";
import "./context-form.scss";

type Row = Record<string, unknown>;

/** The smallest TableModel ViewManager needs from a supplier: the rows by id. */
function supplierModel(): TableModel<Row> {
  return { columns: [], initialRows: [], rowId: (row) => String(row["uid"]), currentRows: new Map() };
}

export function ContextFormView({ view }: { view: SqlFirewallView }) {
  const fields = useMemo(() => view.context ?? [], [view]);
  const [params, setParams] = useSearchParams();
  // per field: the picked reference, or the plain value. Plain values arrive
  // from the route at once; a reference's text is looked up (below).
  const [values, setValues] = useState<Record<string, ReferenceValue | unknown>>(() => {
    const init: Record<string, unknown> = {};
    for (const f of fields) {
      const raw = params.get(f.param);
      if (raw === null || raw === "" || f.reference) continue;
      init[f.name] = f.type === "number" ? Number(raw) : f.type === "boolean" ? raw === "true" : raw;
    }
    return init;
  });
  // One supplier registration per field, alive for the view's lifetime.
  const suppliers = useRef(new Map<string, { id: string; model: TableModel<Row>; api: React.MutableRefObject<GridApi | null> }>());

  useEffect(() => {
    const vm = ViewManager.get();
    const mine = suppliers.current;
    for (const f of fields) {
      const id = contextViewId(view.id, f.name);
      const model = supplierModel();
      const api = { current: { getRow: (rowId: unknown) => model.currentRows.get(String(rowId)) } as unknown as GridApi };
      mine.set(f.name, { id, model, api });
      vm.registerView({ kind: "sql-firewall", id, name: f.label }, api, model);
    }
    return () => {
      for (const s of mine.values()) vm.deregisterView(s.id);
      mine.clear();
    };
  }, [view.id, fields]);

  /** Publish one field's value: the supplier's row set and its selection. */
  const publish = (f: ContextFieldDef, v: ReferenceValue | unknown) => {
    const s = suppliers.current.get(f.name);
    if (!s) return;
    s.model.currentRows.clear();
    let rowIds: string[] = [];
    if (f.reference) {
      const rv = v as ReferenceValue | undefined;
      if (rv) {
        // the pick as a selected row of the target: the whole row when looked up, else key + text
        s.model.currentRows.set(rv.key, { ...(rv.row ?? {}), uid: rv.key, [f.reference.targetKey]: rv.key, [f.reference.display]: rv.text });
        rowIds = [rv.key];
      }
    } else if (v !== undefined && v !== null && v !== "") {
      const id = String(v);
      s.model.currentRows.set(id, { uid: id, value: v });
      rowIds = [id];
    }
    ViewManager.get().notifyRowSelectionChanged(s.id, rowIds);
  };

  const set = (f: ContextFieldDef, v: ReferenceValue | unknown) => {
    setValues((prev) => ({ ...prev, [f.name]: v }));
    const rv = v as ReferenceValue | undefined;
    if (f.reference && rv && !rv.row) {
      // A pick from the search list is key + text; publish the whole row, so the
      // page reads the pick like a selected grid row (`job.name`, `job.worker_uid`).
      const target = { table: f.reference.target, key: f.reference.targetKey, display: f.reference.display, columns: f.reference.columns };
      lookupReference(target, rv.key)
        .then((full) => {
          const whole = full ?? rv;
          setValues((prev) => (prev[f.name] === rv ? { ...prev, [f.name]: whole } : prev));
          publish(f, whole);
        })
        .catch((e: unknown) => {
          console.error(`ContextFormView | ${f.name} |`, e);
          publish(f, rv);
        });
    } else {
      publish(f, v);
    }
    const next = new URLSearchParams(params);
    const str = f.reference ? (v as ReferenceValue | undefined)?.key : v === undefined || v === null || v === "" ? undefined : String(v);
    if (str === undefined) next.delete(f.param);
    else next.set(f.param, str);
    setParams(next, { replace: true });
  };

  // On arrival: what the route carries is published — a plain value at once,
  // a reference once its text is looked up — so the page drills in as it did.
  useEffect(() => {
    let cancelled = false;
    for (const f of fields) {
      const raw = params.get(f.param);
      if (raw === null || raw === "") continue;
      if (!f.reference) {
        publish(f, values[f.name]);
        continue;
      }
      const target = { table: f.reference.target, key: f.reference.targetKey, display: f.reference.display, columns: f.reference.columns };
      lookupReference(target, raw)
        .then((rv) => {
          if (cancelled) return;
          const v = rv ?? { key: raw, text: raw };
          setValues((prev) => ({ ...prev, [f.name]: v }));
          publish(f, v);
        })
        .catch((e: unknown) => console.error(`ContextFormView | ${f.name} |`, e));
    }
    return () => {
      cancelled = true;
    };
    // the route is read once, when the view mounts; afterwards this view writes it
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view.id, fields]);

  const control = (f: ContextFieldDef) => {
    const v = values[f.name];
    if (f.reference) {
      return (
        <ReferencePicker
          target={{ table: f.reference.target, key: f.reference.targetKey, display: f.reference.display, columns: f.reference.columns }}
          label={f.label}
          name={f.name}
          value={v as ReferenceValue | undefined}
          onChange={(rv) => set(f, rv)}
        />
      );
    }
    switch (f.type) {
      case "boolean":
        return (
          <label className="context-form-check">
            <input type="checkbox" checked={Boolean(v)} onChange={(e) => set(f, e.target.checked ? true : undefined)} /> {f.label}
          </label>
        );
      case "number":
        return (
          <input
            type="number"
            placeholder={f.label}
            value={v === undefined || v === null ? "" : String(v)}
            onChange={(e) => set(f, e.target.value === "" ? undefined : Number(e.target.value))}
          />
        );
      default:
        return (
          <input
            type="text"
            placeholder={f.label}
            value={v == null ? "" : String(v)}
            onChange={(e) => set(f, e.target.value === "" ? undefined : e.target.value)}
          />
        );
    }
  };

  return (
    <div className={`_qubes_view context-form ${view.name || ""}`} data-testid={`context-form-${view.id}`}>
      {view.name ? <h1 style={{ marginTop: 0, marginBottom: 0 }}>{view.name}</h1> : null}
      <div className="context-form-fields">
        {fields.map((f) => (
          <div className="context-form-field" key={f.name} data-field={f.name}>
            {f.reference ? control(f) : (
              <>
                {f.type !== "boolean" ? <label>{f.label}</label> : null}
                {control(f)}
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
